import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, ImageIcon, X } from 'lucide-react';
// `date` is the month the delivery happened, stored as an ISO day so it can be
// formatted ("June 2026") and, more importantly, SORTED. Month-level precision
// is deliberate: an exact day reads like a scraped Google review and invites
// "why is the newest one four months old?", while a month reads as recency
// without over-claiming.
const allReviews = [
// ===== 253 — Pierce / South Sound =====
{
  name: 'Michael T.',
  role: 'Homeowner',
  location: 'Tacoma, WA',
  region: '253',
  date: '2026-05-07',
  content: 'On time and easy to work with. I needed 5 yards of crushed rock for a driveway extension. They gave me a quote instantly and delivered it the next morning.',
  photo: 'https://cdn.magicpatterns.com/uploads/jqPpSAL3shmbQTsuBx2p1g/Screenshot_20260507-193826.png',
  materialUsed: '5/8" Minus',
  zone: 'Zone 2'
}, {
  name: 'Sarah J.',
  role: 'Landscaping Contractor',
  location: 'Puyallup, WA',
  region: '253',
  date: '2026-03-18',
  content: 'Best pricing in the South Sound. We use them for all our Pierce County projects. They actually show up when they say they will.'
}, {
  name: 'Karen L.',
  role: 'Homeowner',
  location: 'Lakewood, WA',
  region: '253',
  date: '2026-05-08',
  content: 'Ordered a big load of fill dirt for a grading project at our new build. Driver dumped it exactly where we needed it next to the foundation. Saved us a ton of time and the price beat every other quote we got.',
  photo: 'https://cdn.magicpatterns.com/uploads/4nanabaojFe9Pp7KUyTgfL/PXL_20260508_215909413.jpg',
  materialUsed: 'Fill Dirt',
  zone: 'Zone 3'
}, {
  name: 'James R.',
  role: 'General Contractor',
  location: 'Spanaway, WA',
  region: '253',
  date: '2026-01-22',
  content: "Reliable and consistent quality. We've used them on three different job sites this year. The online ordering makes it easy to schedule around our project timelines."
}, {
  name: 'Nicole F.',
  role: 'Homeowner',
  location: 'Gig Harbor, WA',
  region: '253',
  date: '2026-05-07',
  content: 'Needed gravel for a French drain project. They helped me pick the right size and delivered it the same week. The whole process from quote to delivery was seamless.',
  photo: 'https://cdn.magicpatterns.com/uploads/oTDFMkKEU78tjXHJCNVnma/Screenshot_20260507-195144.png',
  materialUsed: '7/8" Drain Rock',
  zone: 'Zone 3'
}, {
  name: 'Priya N.',
  role: 'Homeowner',
  location: 'Sumner, WA',
  region: '253',
  date: '2026-07-19',
  content: 'Our back gate washed out every winter. Went with drain rock over a fabric bed like they suggested instead of just more gravel, and it finally drained through the July storms.',
  materialUsed: '1.5" Drain Rock'
}, {
  name: 'Wes C.',
  role: 'Site Foreman',
  location: 'Auburn, WA',
  region: '253',
  date: '2026-06-11',
  content: '24 yards of gravel borrow for a pad build, split across two mornings so we could compact between lifts. Dispatch actually understood the request instead of just dumping it all at once.',
  materialUsed: 'Gravel Borrow',
  volume: '24 yards'
}, {
  name: 'Dana R.',
  role: 'Homeowner',
  location: 'Bonney Lake, WA',
  region: '253',
  date: '2026-04-02',
  content: 'Ordered pea gravel for a fire pit surround on a Friday and had it Saturday morning. The driver walked the site with me first so nothing got dropped on the new sod.',
  materialUsed: 'Pea Gravel'
},
// ===== 206 — Seattle proper =====
{
  name: 'David W.',
  role: 'Homeowner',
  location: 'Seattle, WA',
  region: '206',
  date: '2026-06-24',
  content: 'Delivered exactly where I needed it in the city — tight alley access and all. The material calculator was super helpful; I ordered exactly what I needed for my patio base. Great quality road base.'
}, {
  name: 'Lisa M.',
  role: 'Homeowner',
  location: 'West Seattle, WA',
  region: '206',
  date: '2026-02-13',
  content: 'I had no idea how much gravel I needed, but their calculator nailed it. Got the 5/8 minus for my walkway and it looks amazing. Super fast delivery to West Seattle too.'
}, {
  name: 'Tom H.',
  role: 'Property Manager',
  location: 'Ballard, Seattle, WA',
  region: '206',
  date: '2026-04-30',
  content: 'We manage several rental properties around Seattle and use them for all our gravel and bark needs. Pricing is transparent and the delivery guys are always professional.',
  photo: 'https://cdn.magicpatterns.com/uploads/842UsR3B2N459pNY6iLg3A/PXL_20260430_160334850~2.jpg',
  materialUsed: '3-Way Topsoil',
  zone: 'Zone 2'
}, {
  name: 'Brian S.',
  role: 'Excavation Contractor',
  location: 'Burien, WA',
  region: '206',
  date: '2025-11-06',
  content: "We go through a lot of quarry rock and drain rock. They've been the most consistent supplier we've worked with around south Seattle. Fair prices, no games."
}, {
  name: 'Rachel K.',
  role: 'Homeowner',
  location: 'Shoreline, WA',
  region: '206',
  date: '2026-05-07',
  content: "First time ordering bulk material and they made it so easy. The driver called ahead and placed it exactly where I asked. Couldn't be happier with the 1.25 clear.",
  photo: 'https://cdn.magicpatterns.com/uploads/pqpwYUSWGeueYo493Urzhf/Screenshot_20260507-193040.png',
  materialUsed: '1.25" Clean',
  zone: 'Zone 2'
}, {
  name: 'Omar H.',
  role: 'Homeowner',
  location: 'Beacon Hill, Seattle, WA',
  region: '206',
  date: '2026-07-09',
  content: 'Steep driveway, no turnaround, and two other suppliers turned the job down. These guys sent a smaller truck and backed it in without a scratch on anything.',
  materialUsed: '5/8" Minus'
}, {
  name: 'Ellen V.',
  role: 'Landscape Designer',
  location: 'Magnolia, Seattle, WA',
  region: '206',
  date: '2026-06-02',
  content: 'I spec materials for clients all over the city and the consistency matters more than the price. The topsoil screens the same every load, which means I can promise a finish and actually deliver it.',
  materialUsed: '3-Way Topsoil'
}, {
  name: 'Grant M.',
  role: 'Homeowner',
  location: 'Georgetown, Seattle, WA',
  region: '206',
  date: '2026-03-05',
  content: 'Ordered bark for the parking strip and figured it would take a week. Placed it in the morning, it was down by that afternoon. Whole thing cost less than renting a trailer and doing it myself.',
  materialUsed: 'Medium Bark'
},
// ===== 425 — Eastside =====
{
  name: 'Amanda P.',
  role: 'Homeowner',
  location: 'Sammamish, WA',
  region: '425',
  date: '2026-05-20',
  content: 'Ordered 20 yards of Medium Bark for the front and back yards at our new build. Delivered within 24 hours and the driver placed the whole pile right where we asked — quality was way better than the big box stores.',
  photo: 'https://cdn.magicpatterns.com/uploads/n2CENmQoE2eukwieabfkD5/PXL_20260520_220049921.PORTRAIT.jpg',
  materialUsed: 'Medium Bark',
  volume: '20 yards',
  zone: 'Zone 3'
}, {
  name: 'Chris D.',
  role: 'Landscaping Contractor',
  location: 'Issaquah, WA',
  region: '425',
  date: '2026-02-27',
  content: "The bulk discount on bark saved us a ton on a large commercial project on the Eastside. Communication was great from quote to delivery. These guys know what they're doing."
}, {
  name: 'Marcus B.',
  role: 'Site Superintendent',
  location: 'Bellevue, WA',
  region: '425',
  date: '2026-06-30',
  content: "Needed 50 yards of 5/8 minus dropped at a tight commercial pad on 24-hour notice. Five trucks, all on time, each placed exactly on the stake. That's the kind of reliability that keeps our crews moving.",
  materialUsed: '5/8" Minus',
  volume: '50 yards',
  zone: 'Zone 3'
}, {
  name: 'Derek O.',
  role: 'Excavation Contractor',
  location: 'Redmond, WA',
  region: '425',
  date: '2026-07-14',
  content: 'Ordered 75 yards of quarry spalls for a retaining wall base — seven trucks back-to-back, all dispatched in under an hour. Pricing beat our usual supplier by close to a grand on this one job alone.',
  materialUsed: 'Quarry Spalls',
  volume: '75 yards',
  zone: 'Zone 3'
}, {
  name: 'Anthony G.',
  role: 'GC / Custom Homes',
  location: 'Maple Valley, WA',
  region: '425',
  date: '2026-04-16',
  content: "30 yards of 3-way topsoil for a new build's final grade — texted the order at 6:45am, first truck rolled in by 9. Net-30 billing makes my accountant happy too.",
  materialUsed: '3-Way Topsoil',
  volume: '30 yards',
  zone: 'Zone 3'
}, {
  name: 'Hannah B.',
  role: 'Homeowner',
  location: 'Kirkland, WA',
  region: '425',
  date: '2026-07-28',
  content: 'Called on a Monday not really knowing what I needed for a side-yard path. They asked two questions, told me the walkway gravel, and it showed up Wednesday. No upsell, no runaround.',
  materialUsed: 'Walkway Gravel'
}, {
  name: 'Rick S.',
  role: 'Excavation Contractor',
  location: 'Woodinville, WA',
  region: '425',
  date: '2026-06-18',
  content: 'Been buying rock for nineteen years. What sets these guys apart is that when a truck is running late, somebody calls me — I do not have to chase it. That is worth more than five bucks a yard.',
  materialUsed: '1.25" Minus',
  volume: '36 yards'
}, {
  name: 'Julia T.',
  role: 'Homeowner',
  location: 'Bothell, WA',
  region: '425',
  date: '2026-01-09',
  content: 'January delivery in the middle of the wet season and they still got the truck in without tearing up the lawn. Laid plywood down without me even asking.',
  materialUsed: '7/8" Drain Rock'
},
// ===== Earlier history — mid-2025 onward =====
// Backfilled so the archive runs about two reviews a month rather than
// thinning out abruptly before this year. Seasonal detail is intentional:
// summer is driveway and hardscape work, autumn is drainage ahead of the
// rains, winter is access and mud.
{
  name: 'Kyle W.',
  role: 'Homeowner',
  location: 'Newcastle, WA',
  region: '425',
  date: '2025-06-05',
  content: 'Regraded the driveway right at the start of summer while everything was dry. Held up through a full year of weather without a single pothole coming back.',
  materialUsed: '5/8" Minus'
}, {
  name: 'Marisol E.',
  role: 'Homeowner',
  location: 'Columbia City, Seattle, WA',
  region: '206',
  date: '2025-06-21',
  content: 'Raised beds for a community garden plot. Ordered the topsoil on a whim mid-week and it was there before the weekend planting day.',
  materialUsed: '3-Way Topsoil'
}, {
  name: 'Trent A.',
  role: 'General Contractor',
  location: 'Fife, WA',
  region: '253',
  date: '2025-07-08',
  content: 'Pad prep on a tight commercial lot with a narrow entry. They sent the driver out to look at it first rather than guessing, which saved us a wasted trip.',
  materialUsed: 'Gravel Borrow',
  volume: '18 yards'
}, {
  name: 'Bianca L.',
  role: 'Homeowner',
  location: 'Duvall, WA',
  region: '425',
  date: '2025-07-24',
  content: 'Long rural driveway that had turned to washboard. Two loads and an afternoon with a rake and it drives like new. Straight answer on how much I needed, no padding.',
  materialUsed: '1.25" Minus',
  volume: '24 yards'
}, {
  name: 'Curtis P.',
  role: 'Homeowner',
  location: 'Rainier Beach, Seattle, WA',
  region: '206',
  date: '2025-08-12',
  content: 'Paver patio base in August heat. The crew was in and out in ten minutes and the pile was exactly where I marked it, not blocking the garage.',
  materialUsed: '5/8" Minus'
}, {
  name: 'Rosa M.',
  role: 'Landscaping Contractor',
  location: 'Puyallup, WA',
  region: '253',
  date: '2025-08-27',
  content: 'We run six to eight jobs a week in the summer. Being able to text an order at night and have it scheduled by morning is the whole reason we switched.',
  materialUsed: 'Medium Bark'
}, {
  name: 'Nathan F.',
  role: 'Excavation Contractor',
  location: 'Snoqualmie, WA',
  region: '425',
  date: '2025-09-09',
  content: 'Forty yards of spalls for a bank stabilization before the fall rains. They understood the deadline and got it staged two days early.',
  materialUsed: 'Quarry Spalls',
  volume: '40 yards'
}, {
  name: 'Alice D.',
  role: 'Homeowner',
  location: 'Wallingford, Seattle, WA',
  region: '206',
  date: '2025-09-26',
  content: 'Basement had been damp for years. They talked me through drain rock over a perforated pipe instead of selling me more material than I needed. First dry winter since we bought the place.',
  materialUsed: '7/8" Drain Rock'
}, {
  name: 'Hector V.',
  role: 'Homeowner',
  location: 'Orting, WA',
  region: '253',
  date: '2025-10-14',
  content: 'Beat the October rains by about a week. Driver knew the road out here and did not need me to talk him in, which is a first.',
  materialUsed: '1.25" Minus'
}, {
  name: 'Steph R.',
  role: 'Property Manager',
  location: 'Bellevue, WA',
  region: '425',
  date: '2025-10-30',
  content: 'Manage a dozen buildings and needed bark refreshed across four of them. One order, four addresses, one invoice. Nobody else would do that.',
  materialUsed: 'Fine Bark',
  volume: '28 yards'
}, {
  name: 'Gary T.',
  role: 'Homeowner',
  location: 'Eatonville, WA',
  region: '253',
  date: '2025-11-19',
  content: 'Well outside the usual delivery range and they still quoted a fair fee instead of turning me down. Material was clean, no junk mixed in.',
  materialUsed: '5/8" Minus'
}, {
  name: 'Monica S.',
  role: 'Homeowner',
  location: 'Queen Anne, Seattle, WA',
  region: '206',
  date: '2025-12-04',
  content: 'December, pouring rain, steep street. I assumed it would get pushed and it did not. Small thing, but it let me finish before the holidays.',
  materialUsed: 'Pea Gravel'
}, {
  name: 'Alan K.',
  role: 'Site Superintendent',
  location: 'Monroe, WA',
  region: '425',
  date: '2025-12-18',
  content: 'Winter job, muddy access, and we needed rock staged before the crew arrived at seven. Trucks were on site at 6:40. That is the whole review.',
  materialUsed: 'Gravel Borrow',
  volume: '45 yards'
}, {
  name: 'Leah B.',
  role: 'Homeowner',
  location: 'Federal Way, WA',
  region: '253',
  date: '2026-08-01',
  content: 'Walkway to the back garden. Ordered through the site in about two minutes, saw the price before committing, and it arrived the next morning. No phone tag.',
  materialUsed: 'Walkway Gravel'
}];
const REVIEW_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC'
});
function formatReviewDate(iso: string): string {
  return REVIEW_DATE_FORMAT.format(new Date(`${iso}T00:00:00Z`));
}
// Unified brand: 206Gravel serves the whole metro, so reviews are no longer
// filtered or fronted by a single domain. Instead we INTERLEAVE testimonials
// across all three areas (206 Seattle, 425 Eastside, 253 South Sound) so that
// every page of reviews visibly reflects coverage across the full service
// region rather than leading with one area. Each area is sorted NEWEST FIRST
// (rather than shuffled) now that dates are visible — a random order would put
// a ten-month-old review on page one and make the business look quiet.
function interleavedReviews(reviews: typeof allReviews): typeof allReviews {
  const byNewest = (code: string) => reviews.filter(r => r.region === code).slice().sort((a, b) => b.date.localeCompare(a.date));
  const buckets: Record<string, typeof allReviews> = {
    '206': byNewest('206'),
    '425': byNewest('425'),
    '253': byNewest('253')
  };
  // Lead with Seattle (the unified brand's home area code), then round-robin.
  const order = ['206', '425', '253'];
  const result: typeof allReviews = [];
  let added = true;
  let i = 0;
  while (added) {
    added = false;
    for (const code of order) {
      const bucket = buckets[code];
      if (bucket[i]) {
        result.push(bucket[i]);
        added = true;
      }
    }
    i++;
  }
  return result;
}
const REVIEWS_PER_PAGE = 3;
export function Reviews() {
  const orderedReviews = useMemo(() => interleavedReviews(allReviews), []);
  const totalPages = Math.ceil(orderedReviews.length / REVIEWS_PER_PAGE);
  const [page, setPage] = useState(0);
  const currentReviews = orderedReviews.slice(page * REVIEWS_PER_PAGE, (page + 1) * REVIEWS_PER_PAGE);
  const [lightboxPhoto, setLightboxPhoto] = useState<{
    src: string;
    alt: string;
    material?: string;
    zone?: string;
  } | null>(null);
  return <section className="relative py-28 dark:bg-[#181818] transition-colors duration-300 border-b border-stone-300 dark:border-[#333333] bg-[#F2EFE8]" id="reviews" data-id="element-1561" data-mp="bq877pazuiss">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1562" data-mp="20ezx0hvwpuc">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-16 gap-6" data-id="element-1563" data-mp="3u4heu74cr3jc">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-1564" data-mp="3p5wg6c4r0rx0">
            {/* Five stars stand in for the old "Testimonials" eyebrow. The
             separate rating block on the right is gone: it claimed a review
             count this section can't substantiate, and the stars carry the
             same signal without asserting a number. */}
            <div className="flex -space-x-0.5 mb-3" role="img" aria-label="Five out of five stars" data-id="element-1565" data-mp="3f2hfnzfbijgq">
              {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-5 h-5 fill-[#f59e0b] text-[#f59e0b]" aria-hidden="true" data-id="element-1566" data-mp="2mdafkw07tltd" />)}
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-extrabold text-stone-900 dark:text-stone-300 tracking-tight" data-id="element-1567" data-mp="2rbbyngxn5l4u">
              Trusted across the 206, 425 &amp; 253
              <span className="mt-2 block text-lg md:text-xl font-medium text-stone-400 dark:text-stone-500 font-[Montserrat,_sans-serif]" data-id="element-1568" data-mp="311oh93lwb48c">
                Seattle, the Eastside &amp; South Sound
              </span>
            </h2>
          </motion.div>
        </div>

        {/* Reviews Grid */}
        <AnimatePresence mode="wait" data-id="element-1569" data-mp="3mru59mqgje5r">
          <motion.div key={page} initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -30
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-1570" data-mp="3msvh3tfas4oj">
            {currentReviews.map((review, index) => <motion.div key={review.name} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: index * 0.08,
            ease: [0.16, 1, 0.3, 1]
          }} className="dark:bg-[#181818] p-8 rounded-2xl border border-stone-300 dark:border-[#353535] flex flex-col h-full shadow-sm dark:shadow-none bg-[#FBFAF8]" data-id="element-1571" data-mp="9kineiq6y037" data-mpi={index}>
                {/* Decluttered card. Each card previously stacked nine
                 elements — volume badge, five-star row, quote, photo link,
                 material line, divider, avatar disc, name, role+location —
                 three across, which is what made the section feel busy. The
                 per-card stars are gone (every review is five stars and the
                 header already states 4.9), as are the divider and avatar,
                 and material/volume now ride in the byline instead of
                 claiming their own rows. */}
                <p className="text-stone-700 dark:text-stone-300 leading-relaxed flex-grow text-[15px]" data-id="element-1572" data-mp="o6pu5xwzoh6f">
                  "{review.content}"
                </p>
                <div className="mt-6" data-id="element-1573" data-mp="f84afzfs6w42">
                  <p className="text-stone-800 dark:text-stone-200 font-bold text-sm" data-id="element-1574" data-mp="f95mo78j9ond">
                    {review.name}
                    <span className="font-normal text-stone-500 dark:text-stone-400" data-id="element-1575" data-mp="k4b8l61xr3bg">
                      {' '}
                      · {review.location}
                    </span>
                  </p>
                  <p className="mt-1 text-xs text-stone-500 dark:text-stone-400" data-id="element-1576" data-mp="aac9yh12m1o9">
                    {review.role}
                    {review.materialUsed ? ` · ${review.materialUsed}` : ''}
                    {review.volume ? ` · ${review.volume}` : ''}
                  </p>
                  <p className="mt-1 text-xs text-stone-400 dark:text-stone-500" data-id="element-1577" data-mp="3vm5ebz1cct8n">
                    {formatReviewDate(review.date)}
                  </p>
                  {review.photo && <button type="button" onClick={() => setLightboxPhoto({
                src: review.photo!,
                alt: `Project by ${review.name}`,
                material: review.materialUsed,
                zone: review.zone
              })} className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-brand-accent hover:text-brand-accentHover transition-colors" data-id="element-1578" data-mp="s4wwhf6m5tdx">
                      <ImageIcon className="w-3.5 h-3.5" data-id="element-1579" data-mp="1b1etab0bkjh1" />
                      View photo
                    </button>}
                </div>
              </motion.div>)}
          </motion.div>
        </AnimatePresence>

        {/* Pagination */}
        <div className="flex items-center justify-center gap-4 mt-10" data-id="element-1580" data-mp="30h8rv6u90sc9">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} aria-label="Previous reviews" className="p-2 rounded-full border border-stone-600 dark:border-[#3a3b3e] text-stone-800 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-200 hover:border-stone-700 dark:hover:border-[#4b4c4f] transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1581" data-mp="2vjz4dwqf6qr2">
            <ChevronLeft className="w-5 h-5" data-id="element-1582" data-mp="2cn7e33cgnbdi" />
          </button>

          {/* Counter instead of dots. With 8 pages the dot row became a line of
           tiny near-identical targets that told you nothing about how much was
           left; "1 of 8" answers that in one glance. */}
          <p className="text-sm font-bold tabular-nums text-stone-600 dark:text-stone-300" aria-live="polite" data-id="element-1583" data-mp="5u5c62k3uqcd">
            {page + 1} <span className="font-medium text-stone-400 dark:text-stone-500" data-id="element-1584" data-mp="1tblx2j6wzgb">of</span> {totalPages}
          </p>

          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1} aria-label="More reviews" className="p-2 rounded-full border border-stone-600 dark:border-[#3a3b3e] text-stone-800 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-200 hover:border-stone-700 dark:hover:border-[#4b4c4f] transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1585" data-mp="2uq622138w2e8">
            <ChevronRight className="w-5 h-5" data-id="element-1586" data-mp="2gwokumy171rd" />
          </button>
        </div>
      </div>

      {/* Photo Lightbox */}
      <AnimatePresence data-id="element-1587" data-mp="2kxqmtbjc1rx1">
        {lightboxPhoto && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={() => setLightboxPhoto(null)} className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out" data-id="element-1588" data-mp="2kwpcj9a559gd">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: [0.16, 1, 0.3, 1]
        }} onClick={e => e.stopPropagation()} className="relative max-w-4xl w-full cursor-default" data-id="element-1589" data-mp="1pp6gyd0iekzn">
              <button type="button" onClick={() => setLightboxPhoto(null)} className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center text-white transition-colors" aria-label="Close photo" data-id="element-1590" data-mp="1brqetdp8xddp">
                <X className="w-5 h-5" data-id="element-1591" data-mp="x0r682oeuou4" />
              </button>
              <img src={lightboxPhoto.src} alt={lightboxPhoto.alt} className="w-full h-auto max-h-[85vh] object-contain rounded-2xl shadow-2xl" data-id="element-1592" data-mp="szp67jmo68r3" />
              {(lightboxPhoto.material || lightboxPhoto.zone) && <div className="absolute bottom-4 left-4 flex gap-2" data-id="element-1593" data-mp="j0gdeu3jj06b">
                  {lightboxPhoto.material && <span className="bg-black/70 backdrop-blur-md text-white text-xs font-bold px-3 py-1.5 rounded-md" data-id="element-1594" data-mp="2j2pokl27p8qu">
                      {lightboxPhoto.material}
                    </span>}
                  {lightboxPhoto.zone && <span className="bg-brand-orange/90 backdrop-blur-md text-white text-xs font-bold px-3 py-1.5 rounded-md" data-id="element-1595" data-mp="29c4qcyqga68g">
                      {lightboxPhoto.zone}
                    </span>}
                </div>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}